import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Trash2 } from "lucide-react";
import {
  useLayaways,
  useLayaway,
  useCreateLayaway,
  useRegisterLayawayPayment,
  useCancelLayaway,
  useCompleteLayaway,
  LAYAWAY_STATUS_LABEL,
  LayawayStatus,
  LayawayPaymentMethod,
} from "@/api/layaways";
import { useStoreCustomers, useOpenShift, PAYMENT_METHOD_LABEL, PaymentMethod } from "@/api/pos";
import { useProducts, useBranches } from "@/api/inventory";
import { getApiErrorMessage, getApiErrorCode } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const STATUS_STYLE: Record<LayawayStatus, string> = {
  ABIERTO: "bg-blue-100 text-blue-700",
  COMPLETADO: "bg-green-100 text-green-700",
  CANCELADO: "bg-red-100 text-red-700",
};

const DEPOSIT_METHODS: LayawayPaymentMethod[] = ["EFECTIVO", "TARJETA", "TRANSFERENCIA", "NEQUI", "DAVIPLATA", "OTRO"];
const QUICK_PAYMENT_METHODS: PaymentMethod[] = ["EFECTIVO", "TARJETA", "TRANSFERENCIA", "NEQUI", "DAVIPLATA", "CREDITO"];

/** Apartados de tienda: el cliente separa mercancía con un abono inicial y la termina de pagar en varias visitas. No reserva stock — al completarse crea una venta real del POS. */
export default function LayawaysPage() {
  const [status, setStatus] = useState<LayawayStatus | "">("ABIERTO");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useLayaways({ status: status || undefined, pageSize: 50 });
  const layaways = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Apartados</h1>
          <p className="text-sm text-gray-500">El cliente separa mercancía con un abono inicial y la termina de pagar en varias visitas.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
          <Plus size={16} className="mr-1.5" /> Nuevo apartado
        </button>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as LayawayStatus | "")}>
          <option value="">Todos los estados</option>
          {Object.entries(LAYAWAY_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">#</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Saldo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && layaways.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No hay apartados con este filtro.
                </td>
              </tr>
            )}
            {layaways.map((l) => (
              <tr key={l.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedId(l.id)}>
                <td className="px-4 py-3 text-gray-600">#{l.layawayNumber}</td>
                <td className="px-4 py-3 text-gray-600">{l.startDate}</td>
                <td className="px-4 py-3 text-gray-900">{l.storeCustomer?.name ?? l.customerName ?? "—"}</td>
                <td className="px-4 py-3 text-right text-gray-900">{currency.format(Number(l.principal))}</td>
                <td className="px-4 py-3 text-right font-medium text-gray-900">{currency.format(Number(l.balance))}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[l.status]}`}>{LAYAWAY_STATUS_LABEL[l.status]}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setSelectedId(l.id)}>
                    Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateLayawayModal onClose={() => setShowCreate(false)} />}
      {selectedId && <LayawayDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function LayawayDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: layaway, isLoading } = useLayaway(id);
  const registerPayment = useRegisterLayawayPayment();
  const cancelLayaway = useCancelLayaway();
  const completeLayaway = useCompleteLayaway();
  const { data: openShift } = useOpenShift(layaway?.branchId);

  const [paymentAmount, setPaymentAmount] = useState("0");
  const [paymentMethod, setPaymentMethod] = useState<LayawayPaymentMethod>("EFECTIVO");

  const [completeMethod, setCompleteMethod] = useState<PaymentMethod>("EFECTIVO");
  const [amountPaid, setAmountPaid] = useState("0");

  useEffect(() => {
    if (layaway) {
      setPaymentAmount(String(Number(layaway.balance)));
      setAmountPaid(String(Number(layaway.principal) - Number(layaway.balance)));
    }
  }, [layaway?.id, layaway?.balance, layaway?.principal]);

  const isCredit = completeMethod === "CREDITO";
  const canUseCredit = !!layaway?.storeCustomerId;

  const handleRegisterPayment = async () => {
    if (!(Number(paymentAmount) > 0)) {
      toast.error("El abono debe ser mayor a cero.");
      return;
    }
    try {
      await registerPayment.mutateAsync({ id, amount: paymentAmount, paymentMethod });
      toast.success("Abono registrado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar este apartado? Los abonos ya recibidos quedan en el historial.")) return;
    try {
      await cancelLayaway.mutateAsync(id);
      toast.success("Apartado cancelado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleComplete = async () => {
    if (isCredit && !canUseCredit) {
      toast.error("Para completar a crédito el apartado necesita un cliente registrado.");
      return;
    }
    try {
      await completeLayaway.mutateAsync({ id, paymentMethod: completeMethod, amountPaid });
      toast.success("Apartado completado: se creó la venta.");
      onClose();
    } catch (error) {
      // El precio se recalcula VIGENTE al completar — puede haber quedado por debajo del costo
      // aunque no lo estuviera al apartar (ver pos.service.ts::createSale).
      if (getApiErrorCode(error) === "BELOW_COST_CONFIRMATION_REQUIRED") {
        if (window.confirm(`${getApiErrorMessage(error)}\n\n¿Completar de todas formas?`)) {
          try {
            await completeLayaway.mutateAsync({ id, paymentMethod: completeMethod, amountPaid, acknowledgeBelowCost: true });
            toast.success("Apartado completado: se creó la venta.");
            onClose();
          } catch (retryError) {
            toast.error(getApiErrorMessage(retryError));
          }
        }
        return;
      }
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Apartado {layaway ? `#${layaway.layawayNumber}` : ""}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {layaway && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Cliente</p>
                  <p className="font-medium text-gray-900">{layaway.storeCustomer?.name ?? layaway.customerName ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Sucursal</p>
                  <p className="font-medium text-gray-900">{layaway.branch?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total</p>
                  <p className="font-medium text-gray-900">{currency.format(Number(layaway.principal))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Saldo pendiente</p>
                  <p className="font-semibold text-gray-900">{currency.format(Number(layaway.balance))}</p>
                </div>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">Productos</p>
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-gray-500">Producto</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Cant.</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Precio</th>
                      <th className="px-2 py-1.5 text-right font-medium text-gray-500">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {layaway.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="px-2 py-1.5">{l.product?.name ?? l.productId}</td>
                        <td className="px-2 py-1.5 text-right">{l.quantity}</td>
                        <td className="px-2 py-1.5 text-right">{currency.format(Number(l.unitPrice))}</td>
                        <td className="px-2 py-1.5 text-right">{currency.format(Number(l.subtotal))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">Historial de abonos</p>
                {layaway.payments.length === 0 ? (
                  <p className="text-xs text-gray-400">Sin abonos.</p>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-500">Fecha</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-500">Método</th>
                        <th className="px-2 py-1.5 text-right font-medium text-gray-500">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {layaway.payments.map((p) => (
                        <tr key={p.id}>
                          <td className="px-2 py-1.5">{p.paymentDate}</td>
                          <td className="px-2 py-1.5">{PAYMENT_METHOD_LABEL[p.paymentMethod]}</td>
                          <td className="px-2 py-1.5 text-right">{currency.format(Number(p.amount))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {layaway.notes && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Notas</p>
                  <p className="text-sm text-gray-700">{layaway.notes}</p>
                </div>
              )}

              {layaway.status === "COMPLETADO" && (
                <p className="rounded-md bg-green-50 p-3 text-sm text-green-700">Completado: se creó la venta #{layaway.sale?.saleNumber ?? "—"}.</p>
              )}
              {layaway.status === "CANCELADO" && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">Este apartado fue cancelado.</p>}

              {layaway.status === "ABIERTO" && (
                <>
                  <div className="space-y-2 rounded-md bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Registrar abono</p>
                    <div className="flex flex-wrap gap-1.5">
                      {DEPOSIT_METHODS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={`rounded-md border px-2.5 py-1 text-xs font-medium ${paymentMethod === m ? "border-brand-600 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-600"}`}
                          onClick={() => setPaymentMethod(m)}
                        >
                          {PAYMENT_METHOD_LABEL[m]}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input className="input-field" type="number" placeholder="Monto del abono" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} />
                      <button type="button" className="btn-secondary" onClick={() => setPaymentAmount(String(Number(layaway.balance)))}>
                        Saldar todo
                      </button>
                    </div>
                    <button type="button" className="btn-primary w-full" disabled={registerPayment.isPending} onClick={handleRegisterPayment}>
                      {registerPayment.isPending ? "Guardando..." : "Registrar abono"}
                    </button>
                  </div>

                  <div className="space-y-2 rounded-md bg-gray-50 p-3">
                    <p className="text-sm font-medium text-gray-700">Completar apartado (el cliente se lleva la mercancía)</p>
                    {!openShift && (
                      <p className="text-xs text-amber-700">
                        No hay un turno de caja abierto en {layaway.branch?.name ?? "esta sucursal"}. Abre un turno en el POS antes de completar.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {QUICK_PAYMENT_METHODS.filter((m) => m !== "CREDITO" || canUseCredit).map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={`rounded-md border px-2.5 py-1 text-xs font-medium ${completeMethod === m ? "border-brand-600 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-600"}`}
                          onClick={() => setCompleteMethod(m)}
                        >
                          {PAYMENT_METHOD_LABEL[m]}
                        </button>
                      ))}
                    </div>
                    <input className="input-field" type="number" placeholder="Monto pagado" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} />
                    <button type="button" className="btn-primary w-full" disabled={!openShift || completeLayaway.isPending} onClick={handleComplete}>
                      {completeLayaway.isPending ? "Completando..." : "Completar apartado"}
                    </button>
                  </div>

                  <button type="button" className="btn-secondary w-full" disabled={cancelLayaway.isPending} onClick={handleCancel}>
                    Cancelar apartado
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface DraftLine {
  productId: string;
  quantity: string;
}

function CreateLayawayModal({ onClose }: { onClose: () => void }) {
  const { data: branches } = useBranches(true);
  const { data: productsResponse } = useProducts({ pageSize: 200, isActive: true });
  const products = productsResponse?.data ?? [];
  const createLayaway = useCreateLayaway();

  const [branchId, setBranchId] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerLabel, setCustomerLabel] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([{ productId: "", quantity: "1" }]);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositMethod, setDepositMethod] = useState<LayawayPaymentMethod>("EFECTIVO");
  const customerInputRef = useRef<HTMLInputElement>(null);

  const { data: customerMatches } = useStoreCustomers(customerSearch.length >= 2 ? customerSearch : undefined);

  useEffect(() => {
    if (!branchId && branches && branches.length > 0) {
      setBranchId(branches.find((b) => b.isMain)?.id ?? branches[0].id);
    }
  }, [branches, branchId]);

  const addLine = () => setLines((prev) => [...prev, { productId: "", quantity: "1" }]);
  const removeLine = (index: number) => setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const estimatedTotal = lines.reduce((sum, l) => {
    const product = products.find((p) => p.id === l.productId);
    if (!product) return sum;
    return sum + Number(product.salePrice) * (Number(l.quantity) || 0);
  }, 0);

  const isValid = branchId && lines.every((l) => l.productId && Number(l.quantity) > 0) && Number(depositAmount) > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Elige sucursal, completa cada línea y define un abono inicial mayor a cero.");
      return;
    }
    try {
      await createLayaway.mutateAsync({
        branchId,
        storeCustomerId: customerId ?? undefined,
        customerName: customerId ? undefined : customerName || undefined,
        customerPhone: customerId ? undefined : customerPhone || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        depositAmount,
        depositMethod,
      });
      toast.success("Apartado creado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Nuevo apartado</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Sucursal</label>
            <select className="input-field mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Seleccione...</option>
              {branches?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Cliente (opcional)</label>
            {customerId ? (
              <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                <span className="truncate text-gray-900">{customerLabel}</span>
                <button type="button" className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => { setCustomerId(null); setCustomerLabel(""); }}>
                  Cambiar
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <input
                    ref={customerInputRef}
                    className="input-field"
                    placeholder="Buscar cliente registrado por nombre o cédula..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                  />
                  {customerSearch.length >= 2 && (customerMatches?.length ?? 0) > 0 && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {customerMatches!.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setCustomerId(c.id);
                            setCustomerLabel(`${c.name}${c.documentId ? ` (${c.documentId})` : ""}`);
                            setCustomerSearch("");
                          }}
                        >
                          {c.name} {c.documentId ? `(${c.documentId})` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400">O, si no tiene cuenta registrada, deja un nombre/teléfono de referencia:</p>
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-field" placeholder="Nombre" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
                  <input className="input-field" placeholder="Teléfono" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Productos</p>
              <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={addLine}>
                + Agregar línea
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="grid grid-cols-12 items-center gap-2">
                  <select className="input-field col-span-8" value={line.productId} onChange={(e) => updateLine(index, { productId: e.target.value })}>
                    <option value="">Producto...</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.sku} — {p.name} ({currency.format(Number(p.salePrice))})
                      </option>
                    ))}
                  </select>
                  <input
                    className="input-field col-span-3"
                    type="number"
                    placeholder="Cantidad"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, { quantity: e.target.value })}
                  />
                  <button type="button" className="col-span-1 text-gray-400 hover:text-red-600" onClick={() => removeLine(index)} aria-label="Quitar línea">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            {estimatedTotal > 0 && <p className="mt-2 text-right text-sm text-gray-500">Total estimado: {currency.format(estimatedTotal)}</p>}
          </div>

          <div className="space-y-2 rounded-md bg-gray-50 p-3">
            <p className="text-sm font-medium text-gray-700">Abono inicial (obligatorio)</p>
            <div className="flex flex-wrap gap-1.5">
              {DEPOSIT_METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`rounded-md border px-2.5 py-1 text-xs font-medium ${depositMethod === m ? "border-brand-600 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-600"}`}
                  onClick={() => setDepositMethod(m)}
                >
                  {PAYMENT_METHOD_LABEL[m]}
                </button>
              ))}
            </div>
            <input className="input-field" type="number" placeholder="Monto del abono inicial" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createLayaway.isPending} onClick={handleSubmit}>
            {createLayaway.isPending ? "Guardando..." : "Crear apartado"}
          </button>
        </div>
      </div>
    </div>
  );
}
